import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Home, ArrowRight, Lock, Unlock, RotateCw, Shield, CheckCircle, RefreshCcw } from 'lucide-react';
import { useAcademyStore } from '../../../store/academyStore';
import { ModuleSuccess } from '../../ui/ModuleSuccess';

const ALPHABET = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";

const MISSIONS = [
    {
        id: 1,
        text: "Mission 1: Encrypt 'ATTACK' with Key 4.",
        hint: "Mode: Encrypt. Input: 'ATTACK'. Shift: 4.",
        check: (input: string, shift: number, mode: 'encrypt' | 'decrypt', output: string) =>
            mode === 'encrypt' && input === 'ATTACK' && shift === 4
    },
    {
        id: 2,
        text: "Mission 2: Decrypt 'IFMMP' (Key 1).",
        hint: "Switch to DECRYPT mode. Type 'IFMMP' as input. Shift: 1.",
        check: (input: string, shift: number, mode: 'encrypt' | 'decrypt', output: string) =>
            mode === 'decrypt' && input === 'IFMMP' && shift === 1
    },
    {
        id: 3,
        text: "Mission 3: ROT13 Cipher (Hide 'OPEN').",
        hint: "Mode: Encrypt. Input: 'OPEN'. Shift: 13.",
        check: (input: string, shift: number, mode: 'encrypt' | 'decrypt', output: string) =>
            mode === 'encrypt' && input === 'OPEN' && shift === 13
    }
];

export const EncryptionModule = () => {
    const { completeModule } = useAcademyStore();
    const [shift, setShift] = useState(3);
    const [input, setInput] = useState("GORILLA");
    const [mode, setMode] = useState<'encrypt' | 'decrypt'>('encrypt');
    const [completed, setCompleted] = useState(false);

    // Mission State
    const [missionIndex, setMissionIndex] = useState(0);
    const [missionComplete, setMissionComplete] = useState(false);
    const currentMission = MISSIONS[missionIndex];

    // Caesar Cipher Logic
    const processText = (text: string, s: number, currentMode: 'encrypt' | 'decrypt') => {
        return text.toUpperCase().split('').map(char => {
            const idx = ALPHABET.indexOf(char);
            if (idx === -1) return char;

            let newIdx;
            if (currentMode === 'encrypt') {
                newIdx = (idx + s) % 26;
            } else {
                newIdx = (idx - s + 26) % 26;
            }
            return ALPHABET[newIdx];
        }).join('');
    };

    const output = processText(input, shift, mode);

    // Check Mission
    useEffect(() => {
        if (missionComplete || completed) return;

        if (currentMission.check(input, shift, mode, output)) {
            setMissionComplete(true);
            setTimeout(() => {
                if (missionIndex < MISSIONS.length - 1) {
                    setMissionIndex(prev => prev + 1);
                    setMissionComplete(false);
                    // Reset defaults for next mission
                    setInput("");
                    setShift(0);
                    // Auto-switch mode for convenience?
                    if (missionIndex === 0) setMode('decrypt'); // Prep for Mission 2
                    if (missionIndex === 1) setMode('encrypt'); // Prep for Mission 3
                } else {
                    setCompleted(true);
                    completeModule('cryptography-basics', 100);
                }
            }, 1500);
        }
    }, [input, shift, mode, output, missionComplete, completed, missionIndex, currentMission, completeModule]);

    return (
        <div className="min-h-screen bg-[#f0f9ff] flex flex-col p-8 font-sans pt-24">

            <div className="text-center mb-8">
                <h1 className="text-4xl font-black font-comic text-slate-800 mb-2 flex flex-col md:flex-row items-center justify-center gap-3">
                    <div className="flex items-center gap-3">
                        <Shield className="text-green-500" size={40} />
                        Secret Message Club
                    </div>
                    <div className="bg-slate-800 text-green-400 px-4 py-1 rounded-full text-sm font-bold border border-green-500 shadow-md">
                        Step {missionIndex + 1}/{MISSIONS.length}
                    </div>
                </h1>

                {/* Mission Banner */}
                <div className={`max-w-2xl mx-auto mb-6 p-4 rounded-xl border-2 transition-all duration-500 ${missionComplete ? 'bg-green-100 border-green-400 scale-105' : 'bg-white border-blue-400 shadow-lg'}`}>
                    <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">
                        {missionComplete ? '✅ MISSION COMPLETE!' : '🎯 CURRENT OBJECTIVE'}
                    </div>
                    <div className={`text-xl font-bold ${missionComplete ? 'text-green-700' : 'text-blue-700'}`}>
                        {completed ? "All Codes Broken!" : currentMission.text}
                    </div>
                    {!completed && !missionComplete && (
                        <div className="text-sm text-slate-400 mt-1 italic">
                            Hint: {currentMission.hint}
                        </div>
                    )}
                </div>
            </div>

            <div className="flex-1 max-w-6xl mx-auto w-full grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">

                {/* LEFT: The Decoder Ring */}
                <div className="bg-white p-8 rounded-3xl shadow-xl border-4 border-slate-200 relative overflow-hidden">
                    <div className="absolute top-0 right-0 p-4 opacity-10 pointer-events-none">
                        <img src="/assets/gorillas/gorilla_secret_agent.png" className="w-64" />
                    </div>

                    <h3 className="font-bold text-xl text-slate-700 mb-6 flex items-center gap-2">
                        <RotateCw className="text-blue-500" /> The Shift Key
                    </h3>

                    <div className="mb-8 p-6 bg-slate-50 rounded-2xl border-2 border-slate-100 flex flex-col items-center">
                        <label className="font-bold text-slate-500 mb-2 uppercase text-xs">Shift Amount (Key)</label>
                        <div className="flex items-center gap-4 w-full max-w-xs">
                            <input
                                type="range"
                                min="0" max="25"
                                value={shift}
                                onChange={e => setShift(Number(e.target.value))}
                                className="flex-1 accent-blue-600 h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer"
                            />
                            <div className="w-12 h-12 bg-blue-600 text-white rounded-xl flex items-center justify-center font-black text-2xl shadow-lg">
                                {shift}
                            </div>
                        </div>
                    </div>

                    {/* Visual Cipher Wheel */}
                    <div className="space-y-4">
                        <div>
                            <div className="text-xs font-bold text-slate-400 mb-1">INPUT ALPHABET</div>
                            <div className="flex bg-slate-100 rounded-lg overflow-hidden border border-slate-200 font-mono text-sm text-slate-500">
                                {ALPHABET.split('').map(char => (
                                    <div key={char} className="flex-1 text-center py-1 border-r last:border-0 border-slate-200">{char}</div>
                                ))}
                            </div>
                        </div>

                        <div className="flex justify-center text-blue-500 font-bold text-xs">{mode === 'encrypt' ? 'SHIFTS TO (+)' : 'SHIFTS BACK (-)'}</div>

                        <div>
                            <div className="text-xs font-bold text-blue-500 mb-1">OUTPUT ALPHABET</div>
                            <div className="flex bg-blue-50 rounded-lg overflow-hidden border border-blue-200 font-mono text-sm font-bold text-blue-700 relative">
                                <motion.div
                                    className="flex w-full"
                                    animate={{ x: 0 }}
                                >
                                    {ALPHABET.split('').map((_, i) => (
                                        <div key={i} className="flex-1 text-center py-1 border-r last:border-0 border-blue-200">
                                            {mode === 'encrypt'
                                                ? ALPHABET[(i + shift) % 26]
                                                : ALPHABET[(i - shift + 26) % 26]
                                            }
                                        </div>
                                    ))}
                                </motion.div>
                            </div>
                        </div>
                    </div>

                </div>

                {/* RIGHT: The Message Pad */}
                <div className="space-y-8">

                    {/* Mode Toggle */}
                    <div className="flex bg-slate-200 p-1 rounded-xl">
                        <button
                            onClick={() => setMode('encrypt')}
                            className={`flex-1 py-3 rounded-lg font-bold flex items-center justify-center gap-2 transition-all ${mode === 'encrypt' ? 'bg-white shadow-md text-blue-600' : 'text-slate-500 hover:bg-slate-300'}`}
                        >
                            <Lock size={18} /> Encrypt
                        </button>
                        <button
                            onClick={() => setMode('decrypt')}
                            className={`flex-1 py-3 rounded-lg font-bold flex items-center justify-center gap-2 transition-all ${mode === 'decrypt' ? 'bg-white shadow-md text-green-600' : 'text-slate-500 hover:bg-slate-300'}`}
                        >
                            <Unlock size={18} /> Decrypt
                        </button>
                    </div>

                    {/* Input */}
                    <div className={`bg-white p-6 rounded-3xl shadow-lg border-2 ${missionComplete ? 'border-green-400' : 'border-slate-100'}`}>
                        <div className="flex justify-between items-center mb-4">
                            <label className="font-bold text-slate-700 flex items-center gap-2">
                                {mode === 'encrypt' ? <Unlock size={18} className="text-green-500" /> : <Lock size={18} className="text-red-500" />}
                                {mode === 'encrypt' ? 'Plain Message (Input)' : 'Encrypted Input'}
                            </label>
                            <span className="text-xs font-bold bg-slate-100 text-slate-500 px-2 py-1 rounded">TYPE HERE</span>
                        </div>
                        <textarea
                            value={input}
                            onChange={(e) => setInput(e.target.value.toUpperCase())}
                            className="w-full bg-slate-50 border-2 border-slate-200 rounded-xl p-4 font-mono text-lg outline-none focus:border-blue-500 text-slate-700 font-bold uppercase transition-all"
                            rows={3}
                            placeholder={mode === 'encrypt' ? "SECRET..." : "QJAAKV..."}
                        />
                    </div>

                    <div className="flex justify-center">
                        <div className={`rounded-full p-2 animate-bounce ${mode === 'encrypt' ? 'bg-blue-600 text-white' : 'bg-green-600 text-white'}`}>
                            ⬇️
                        </div>
                    </div>

                    {/* Output */}
                    <div className="bg-slate-900 p-6 rounded-3xl shadow-2xl border-4 border-slate-700 relative overflow-hidden">
                        <div className="flex justify-between items-center mb-4 relative z-10">
                            <label className="font-bold text-white flex items-center gap-2">
                                {mode === 'encrypt' ? <Lock size={18} className="text-red-500" /> : <Unlock size={18} className="text-green-500" />}
                                {mode === 'encrypt' ? 'Encrypted Result' : 'Decrypted Plaintext'}
                            </label>
                            <span className={`text-xs font-bold px-2 py-1 rounded ${mode === 'encrypt' ? 'bg-red-900 text-red-200' : 'bg-green-900 text-green-200'}`}>
                                {mode === 'encrypt' ? 'UNREADABLE' : 'READABLE'}
                            </span>
                        </div>

                        <div className="bg-black/50 rounded-xl p-4 font-mono text-xl text-green-400 font-bold break-all shadow-inner border border-slate-600 relative z-10 min-h-[100px] flex items-center">
                            {output || "..."}
                        </div>
                    </div>
                </div>

            </div>
            {/* Celebration */}
            {completed && (
                <ModuleSuccess
                    title="Codebreaker"
                    onRetry={() => {
                        setCompleted(false);
                        setMissionIndex(0);
                        setInput("GORILLA");
                        setShift(3);
                        setMode('encrypt');
                    }}
                    nextModulePath="/sql-injection"
                >
                    <div className="bg-slate-100 rounded-2xl p-6 text-left border-2 border-slate-200">
                        <h4 className="font-bold text-slate-800 mb-2">🕵️ Agent Status: Certified</h4>
                        <p className="text-slate-600 mb-4 text-sm">
                            You've mastered the art of the Caesar Cipher.
                        </p>
                        <div className="space-y-2">
                            {MISSIONS.map(m => (
                                <div key={m.id} className="flex items-center gap-2 text-xs text-green-700 bg-green-50 p-2 rounded">
                                    <CheckCircle size={12} /> {m.text}
                                </div>
                            ))}
                        </div>
                    </div>
                </ModuleSuccess>
            )}
        </div>
    );
};
